import Slider from 'rc-slider';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import ReactPlayer from 'react-player';
import { OnProgressProps } from 'react-player/base';
import styles from '../styles/HtsVideo.module.scss';
import { HtsButtonIcon } from './HtsButtonIcon';
import { HtslIcon } from './HtsIcon';
import { HtsTypography } from './HtsTypography';

import 'rc-slider/assets/index.css';
import '../styles/HtsVideo.scss';

interface IHtsVideoProps {
  src: string;
  containerClassName?: string;
  containerHeight?: number;
}

export const HtsVideo: React.FC<IHtsVideoProps> = memo(
  ({ src, containerClassName, containerHeight }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const videoRef = useRef<ReactPlayer>(null);

    const [isPlaying, setPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(100);

    const handlePlay = useCallback(() => {
      const videos = document.querySelectorAll('video');

      videos.forEach((video) => {
        if (video !== videoRef.current?.getInternalPlayer()) {
          video.pause();
        }
      });

      setPlaying((prev) => !prev);
    }, []);

    const handleEnded = useCallback(() => setPlaying(false), []);

    const handleDuration = useCallback((value: number) => setDuration(value), []);
    const handleProgress = useCallback(
      ({ playedSeconds }: OnProgressProps) => setCurrentTime(playedSeconds),
      []
    );

    const changeCurrentTime = useCallback((value: number | number[]) => {
      if (typeof value === 'number' && videoRef.current) {
        videoRef.current.seekTo(value);
        setCurrentTime(value);
      }
    }, []);

    const formatTime = useCallback((timeInSeconds: number) => {
      const minutes = Math.floor(timeInSeconds / 60);
      const seconds = Math.floor(timeInSeconds % 60)
        .toString()
        .padStart(2, '0');

      return `${minutes}:${seconds}`;
    }, []);

    useEffect(() => {
      if (!containerRef.current || !isPlaying) return;

      const observer = new IntersectionObserver(
        ([entry]) => {
          setPlaying(entry.isIntersecting);
        },
        { root: null, threshold: 0 }
      );

      observer.observe(containerRef.current);

      return () => observer.unobserve(containerRef.current as Element);
    }, [isPlaying]);

    return (
      <div
        className={`${styles.container} ${containerClassName ?? ''}`}
        style={{ height: containerHeight }}
        ref={containerRef}
      >
        <ReactPlayer
          stopOnUnmount
          ref={videoRef}
          playing={isPlaying}
          url={src}
          controls={false}
          width="100%"
          height="100%"
          progressInterval={300}
          onDuration={handleDuration}
          onProgress={handleProgress}
          onPause={handleEnded}
          onEnded={handleEnded}
        />

        <div className={styles.controlsContainer}>
          <HtsTypography variant="caption" color="white" fontWeight="500">
            {formatTime(currentTime)}
          </HtsTypography>

          <Slider
            value={currentTime}
            onChange={changeCurrentTime}
            min={0}
            max={duration}
            step={1}
            className={styles.rangeSlider}
          />

          <HtsTypography variant="caption" color="white" fontWeight="500">
            {formatTime(duration)}
          </HtsTypography>
        </div>

        <HtsButtonIcon className={styles.playButton} onClick={handlePlay}>
          <HtslIcon
            icon={isPlaying ? 'stopPlay' : 'play'}
            color="white"
            className={styles.playIcon}
          />
        </HtsButtonIcon>
      </div>
    );
  }
);
